import React, { useEffect, useRef, useState } from "react";
import { DEMO_AIRLINES } from "./demo_airlines.ts";
import { appPath } from "./app_path.ts";
import {
  TRAVEL_PRIORITY_IDS,
  travelPriorityFromPreferences,
  travelPriorityPreferences,
  type ProfilePreferenceValue,
  type TravelPriorityValue,
} from "./demo_profile_priority.ts";

type PreferenceValue = ProfilePreferenceValue;
type TravellerProfilePreference = "prefer" | "prioritize" | "rather_avoid" | "avoid";
type TravellerType = "budget" | "standard" | "road_warrior" | "vip";
type FareClass = "economy" | "premium_economy" | "business" | "first";

type PreferenceDefinition = {
  id: string;
  label: string;
  positiveOnly?: boolean;
};

type PreferenceSection = {
  title: string;
  description: string;
  preferences: PreferenceDefinition[];
};

const PREFERENCE_LEVELS: Array<{ value: PreferenceValue; label: string }> = [
  { value: -2, label: "Avoid" },
  { value: -1, label: "Rather avoid" },
  { value: 0, label: "Neutral" },
  { value: 1, label: "Prefer" },
  { value: 2, label: "Prioritize" },
];
const TRAVELLER_PROFILE_PREFERENCES: Partial<Record<PreferenceValue, TravellerProfilePreference>> = {
  [-2]: "avoid",
  [-1]: "rather_avoid",
  [1]: "prefer",
  [2]: "prioritize",
};
const TRAVEL_PRIORITY_LEVELS: Array<{ value: TravelPriorityValue; label: string; detail?: string }> = [
  { value: -2, label: "Prioritize", detail: "lower prices" },
  { value: -1, label: "Prefer", detail: "lower prices" },
  { value: 0, label: "Balanced" },
  { value: 1, label: "Prefer", detail: "faster" },
  { value: 2, label: "Prioritize", detail: "faster" },
];

const PREFERENCE_SECTIONS: PreferenceSection[] = [
  {
    title: "Priorities",
    description: "Balance ticket price against journey speed, then set other priorities.",
    preferences: [
      { id: "lower-emissions", label: "Lower emissions", positiveOnly: true },
    ],
  },
  {
    title: "Route",
    description: "Set preferences for how the itinerary is put together.",
    preferences: [
      { id: "airport-transfers", label: "Avoid airport transfers", positiveOnly: true },
      { id: "same-airport-return", label: "Return to the same airport", positiveOnly: true },
      { id: "codeshare-flights", label: "Avoid codeshare flights", positiveOnly: true },
      { id: "shorter-layovers", label: "Shorter layovers", positiveOnly: true },
    ],
  },
  {
    title: "Schedule",
    description: "Choose how you feel about overnight flying.",
    preferences: [{ id: "redeye-flights", label: "Redeye flights" }],
  },
  {
    title: "Carrier type",
    description: "Set your preference for different airline models.",
    preferences: [
      { id: "ultra-low-cost", label: "Ultra-low-cost airlines" },
      { id: "low-cost", label: "Low-cost airlines" },
      { id: "full-service", label: "Full-service airlines" },
    ],
  },
  {
    title: "Alliances",
    description: "Favor or avoid any of the three global alliances.",
    preferences: [
      { id: "alliance-skyteam", label: "SkyTeam" },
      { id: "alliance-star", label: "Star Alliance" },
      { id: "alliance-oneworld", label: "oneworld" },
    ],
  },
  {
    title: "Fare and bags",
    description: "Balance flexibility and included baggage.",
    preferences: [
      { id: "ticket-changes", label: "Ticket changes allowed" },
      { id: "refundable-fares", label: "Refundable fares" },
      { id: "carry-on-included", label: "Carry-on included" },
      { id: "checked-bag-included", label: "Checked bag included" },
    ],
  },
  {
    title: "Comfort",
    description: "Choose which onboard amenities matter.",
    preferences: [
      { id: "wifi", label: "In-flight Wi-Fi", positiveOnly: true },
      { id: "power", label: "In-seat power", positiveOnly: true },
    ],
  },
];

const TRAVELLER_TYPE_OPTIONS: Array<{ value: TravellerType; label: string }> = [
  { value: "budget", label: "Budget" },
  { value: "standard", label: "Standard" },
  { value: "road_warrior", label: "Road warrior" },
  { value: "vip", label: "VIP" },
];
const FARE_CLASS_OPTIONS: Array<{ value: FareClass; label: string }> = [
  { value: "economy", label: "Economy" },
  { value: "premium_economy", label: "Premium economy" },
  { value: "business", label: "Business" },
  { value: "first", label: "First" },
];

const PERSONA_PRESETS: Record<TravellerType, Record<string, PreferenceValue>> = {
  budget: {
    "lower-fares": 2,
    "redeye-flights": 1,
    "ultra-low-cost": 2,
    "low-cost": 2,
    "full-service": -1,
    "carry-on-included": 1,
    "checked-bag-included": 1,
  },
  standard: {
    "lower-emissions": 1,
    "airport-transfers": 1,
    "same-airport-return": 1,
    "codeshare-flights": 1,
    "shorter-layovers": 1,
    "redeye-flights": -1,
    "full-service": 1,
    "ticket-changes": 1,
    "refundable-fares": 1,
    "carry-on-included": 1,
    "checked-bag-included": 1,
    wifi: 1,
    power: 1,
  },
  road_warrior: {
    "shorter-journeys": 2,
    "fewer-stops": 2,
    "lower-emissions": 1,
    "airport-transfers": 2,
    "same-airport-return": 1,
    "codeshare-flights": 2,
    "shorter-layovers": 2,
    "ultra-low-cost": -2,
    "low-cost": -1,
    "full-service": 2,
    "ticket-changes": 2,
    "refundable-fares": 2,
    "carry-on-included": 1,
    "checked-bag-included": 1,
    wifi: 2,
    power: 2,
  },
  vip: {
    "shorter-journeys": 2,
    "fewer-stops": 2,
    "lower-emissions": 1,
    "airport-transfers": 2,
    "same-airport-return": 2,
    "codeshare-flights": 2,
    "shorter-layovers": 2,
    "redeye-flights": -1,
    "ultra-low-cost": -2,
    "low-cost": -2,
    "full-service": 2,
    "ticket-changes": 2,
    "refundable-fares": 2,
    "carry-on-included": 2,
    "checked-bag-included": 2,
    wifi: 2,
    power: 2,
  },
};

const PROFILE_COOKIE_NAME = "reagent_demo_profile";
const PROFILE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const ADDITIONAL_PREFERENCES_MAX_LENGTH = 1000;
const TRAVELLER_SUMMARY_MAX_LENGTH = 20_000;
const TRAVEL_HISTORY_MAX_LENGTH = 50_000;
const PROFILE_PREFERENCE_VALUES: Record<TravellerProfilePreference, PreferenceValue> = {
  avoid: -2,
  rather_avoid: -1,
  prefer: 1,
  prioritize: 2,
};
const VALID_PREFERENCE_IDS = new Set([
  ...PREFERENCE_SECTIONS.flatMap((section) => section.preferences.map((preference) => preference.id)),
  ...TRAVEL_PRIORITY_IDS,
]);
const POSITIVE_ONLY_PREFERENCE_IDS = new Set([
  ...PREFERENCE_SECTIONS.flatMap((section) => (
    section.preferences.filter((preference) => preference.positiveOnly).map((preference) => preference.id)
  )),
  ...TRAVEL_PRIORITY_IDS,
]);
const VALID_AIRLINE_CODES = new Set<string>(DEMO_AIRLINES.map((airline) => airline.value));
const POPULAR_AIRLINE_CODES = [
  "AA", "AC", "AF", "BA", "CX", "DL", "EK", "JL", "KE", "KL",
  "LH", "NH", "NZ", "QF", "QR", "SQ", "TK", "UA", "VS", "WN",
];
const POPULAR_AIRLINE_CODE_SET = new Set(POPULAR_AIRLINE_CODES);
const POPULAR_AIRLINES = POPULAR_AIRLINE_CODES.flatMap((code) => {
  const airline = DEMO_AIRLINES.find((option) => option.value === code);
  return airline ? [airline] : [];
});
const ORDERED_AIRLINES = [
  ...POPULAR_AIRLINES,
  ...DEMO_AIRLINES.filter((airline) => !POPULAR_AIRLINE_CODE_SET.has(airline.value)),
];

type StoredProfile = {
  travellerType: TravellerType;
  fareClass: FareClass;
  personaConfirmed: boolean;
  additionalPreferences: string;
  preferences: Record<string, PreferenceValue>;
  selectedAirlines: string[];
  normalizationOffsets: number[];
};

function emptyProfile(): StoredProfile {
  return {
    travellerType: "standard",
    fareClass: "economy",
    personaConfirmed: false,
    additionalPreferences: "",
    preferences: { ...PERSONA_PRESETS.standard },
    selectedAirlines: [],
    normalizationOffsets: [],
  };
}

function isPreferenceValue(value: unknown): value is PreferenceValue {
  return Number.isInteger(value) && Number(value) >= -2 && Number(value) <= 2;
}

function isTravellerType(value: unknown): value is TravellerType {
  return TRAVELLER_TYPE_OPTIONS.some((option) => option.value === value);
}

function isFareClass(value: unknown): value is FareClass {
  return FARE_CLASS_OPTIONS.some((option) => option.value === value);
}

function readStoredProfile(): StoredProfile {
  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${PROFILE_COOKIE_NAME}=`))
    ?.slice(PROFILE_COOKIE_NAME.length + 1);
  if (!rawCookie) return emptyProfile();

  try {
    const parsed = JSON.parse(decodeURIComponent(rawCookie));
    if (parsed?.v !== 2 || typeof parsed?.p !== "object" || !Array.isArray(parsed?.a)) return emptyProfile();

    const selectedAirlines = [...new Set(parsed.a.filter((code: unknown): code is string => (
      typeof code === "string" && VALID_AIRLINE_CODES.has(code)
    )))];
    const preferences: Record<string, PreferenceValue> = {};
    const normalizationOffsets = Array.isArray(parsed.o) ? parsed.o.map((value: unknown) => (
      typeof value === "number" && Number.isFinite(value) ? value : 0
    )) : [];

    for (const [id, value] of Object.entries(parsed.p)) {
      const validId = VALID_PREFERENCE_IDS.has(id) || (id.startsWith("airline-") && VALID_AIRLINE_CODES.has(id.slice(8)));
      const validDirection = !POSITIVE_ONLY_PREFERENCE_IDS.has(id) || Number(value) >= 0;
      if (validId && validDirection && isPreferenceValue(value)) preferences[id] = value;
    }

    return {
      travellerType: isTravellerType(parsed.t) ? parsed.t : "standard",
      fareClass: isFareClass(parsed.f) ? parsed.f : "economy",
      personaConfirmed: parsed.c === true,
      additionalPreferences: typeof parsed.n === "string" ? parsed.n.slice(0, ADDITIONAL_PREFERENCES_MAX_LENGTH) : "",
      preferences,
      selectedAirlines,
      normalizationOffsets,
    };
  } catch {
    return emptyProfile();
  }
}

export function readTravellerProfile() {
  const storedProfile = readStoredProfile();
  const features = Object.fromEntries(Object.entries(storedProfile.preferences).flatMap(([id, value]) => {
    const preference = TRAVELLER_PROFILE_PREFERENCES[value];
    return preference ? [[id, preference]] : [];
  }));

  return {
    traveller_type: storedProfile.travellerType,
    default_fare_class: storedProfile.fareClass,
    ...(storedProfile.additionalPreferences.trim() ? { additional_preferences: storedProfile.additionalPreferences.trim() } : {}),
    features,
    ...(storedProfile.normalizationOffsets.length ? { normalization_offsets: storedProfile.normalizationOffsets } : {}),
  };
}

function storeProfile({ travellerType, fareClass, personaConfirmed, additionalPreferences, preferences, selectedAirlines, normalizationOffsets }: StoredProfile) {
  const nonNeutralPreferences = Object.fromEntries(Object.entries(preferences).filter(([, value]) => value !== 0));
  const value = encodeURIComponent(JSON.stringify({
    v: 2,
    t: travellerType,
    f: fareClass,
    c: personaConfirmed,
    n: additionalPreferences,
    p: nonNeutralPreferences,
    a: selectedAirlines,
    o: normalizationOffsets,
  }));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${PROFILE_COOKIE_NAME}=${value}; Path=/; Max-Age=${PROFILE_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function storeImportedProfile(profile: any) {
  const current = readStoredProfile();
  const preferences = { ...current.preferences };
  const selectedAirlines = new Set(current.selectedAirlines);

  if (profile?.features && typeof profile.features === "object") {
    for (const [id, preference] of Object.entries(profile.features)) {
      if (typeof preference !== "string" || !(preference in PROFILE_PREFERENCE_VALUES)) continue;
      const validId = VALID_PREFERENCE_IDS.has(id) || (id.startsWith("airline-") && VALID_AIRLINE_CODES.has(id.slice(8)));
      if (!validId) continue;
      preferences[id] = PROFILE_PREFERENCE_VALUES[preference as TravellerProfilePreference];
      if (id.startsWith("airline-")) selectedAirlines.add(id.slice(8));
    }
  }

  const normalizationOffsets = profile?.normalization_offsets && typeof profile.normalization_offsets === "object" ? Object.values(
    profile.normalization_offsets,
  ).filter((value): value is number => typeof value === "number" && Number.isFinite(value)) : current.normalizationOffsets;

  storeProfile({
    travellerType: isTravellerType(profile?.traveller_type) ? profile.traveller_type : current.travellerType,
    fareClass: isFareClass(profile?.default_fare_class) ? profile.default_fare_class : current.fareClass,
    personaConfirmed: current.personaConfirmed,
    additionalPreferences: typeof profile?.additional_preferences === "string" ? profile.additional_preferences.slice(0, ADDITIONAL_PREFERENCES_MAX_LENGTH) : current.additionalPreferences,
    preferences,
    selectedAirlines: [...selectedAirlines],
    normalizationOffsets,
  });
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

export function ProfileMenu({ disabled, menuButtonRef, onAdjustProfile, onImportProfile, onReset }: {
  disabled?: boolean;
  menuButtonRef: React.RefObject<HTMLElement | null>;
  onAdjustProfile: () => void;
  onImportProfile: () => void;
  onReset: () => void;
}) {
  const detailsRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    const closeMenu = () => detailsRef.current?.removeAttribute("open");
    const onPointerDown = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) closeMenu();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const menuAction = (action: () => void) => () => {
    detailsRef.current?.removeAttribute("open");
    action();
  };
  const itemClass = "flex w-full items-center rounded-xl px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-white/10 dark:focus-visible:ring-white/20";

  return (
    <details ref={detailsRef} className="group relative shrink-0">
      <summary ref={menuButtonRef} aria-label="Open demo menu" className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white dark:focus-visible:ring-gray-500 [&::-webkit-details-marker]:hidden">
        <span aria-hidden="true" className="text-xl leading-none">...</span>
      </summary>
      <div className="absolute right-0 top-full z-30 mt-2 w-48 rounded-2xl border border-gray-200 bg-white p-1.5 text-left shadow-xl dark:border-white/10 dark:bg-[#252525]">
        <button type="button" onClick={menuAction(onAdjustProfile)} className={itemClass}>Adjust profile</button>
        <button type="button" onClick={menuAction(onImportProfile)} className={itemClass}>Import profile</button>
        <button type="button" disabled={disabled} onClick={menuAction(onReset)} className={itemClass}>Reset conversation</button>
      </div>
    </details>
  );
}

export function ImportProfileDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: () => void }) {
  const [travellerSummary, setTravellerSummary] = useState("");
  const [travelHistory, setTravelHistory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasInput = travellerSummary.trim().length > 0 || travelHistory.trim().length > 0;

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  if (!open) return null;

  async function importProfile(event: React.FormEvent) {
    event.preventDefault();
    if (!hasInput) {
      setError("Enter a traveler summary or travel history.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(appPath("/demo/profile-import"), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ traveller_summary: travellerSummary, travel_history: travelHistory, traveller_profile: readTravellerProfile() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Could not import the profile.");
      storeImportedProfile(data.traveller_profile);
      onImported();
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Could not import the profile.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-3 backdrop-blur-sm dark:bg-black/70 sm:p-5" onMouseDown={(event) => event.target === event.currentTarget && !submitting && onClose()}>
      <form onSubmit={importProfile} role="dialog" aria-modal="true" aria-labelledby="import-profile-title" className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-[#f7f7f5] text-gray-900 shadow-2xl dark:border-white/10 dark:bg-[#171717] dark:text-gray-100">
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white/90 px-4 py-3 dark:border-white/10 dark:bg-[#1f1f1f]/95 sm:px-5">
          <div>
            <h2 id="import-profile-title" className="text-xl font-semibold tracking-tight">Import profile</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Translate an existing profile or travel history into Reagent preferences.</p>
          </div>
          <button ref={closeButtonRef} type="button" disabled={submitting} onClick={onClose} aria-label="Close profile import" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"><span aria-hidden="true">x</span></button>
        </header>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5">
          <label className="block text-sm font-semibold">
            Traveler Summary
            <textarea value={travellerSummary} maxLength={TRAVELLER_SUMMARY_MAX_LENGTH} rows={7} onChange={(event) => { setTravellerSummary(event.target.value); setError(null); }} className="mt-2 block w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-white/15 dark:bg-white/5 dark:focus:border-blue-400 dark:focus:ring-blue-400/15" />
            <span className="mt-1 block text-right text-[11px] font-normal tabular-nums text-gray-500">{travellerSummary.length} / {TRAVELLER_SUMMARY_MAX_LENGTH}</span>
          </label>
          <label className="block text-sm font-semibold">
            Travel History
            <textarea value={travelHistory} maxLength={TRAVEL_HISTORY_MAX_LENGTH} rows={10} onChange={(event) => { setTravelHistory(event.target.value); setError(null); }} className="mt-2 block w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-normal leading-5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-white/15 dark:bg-white/5 dark:focus:border-blue-400 dark:focus:ring-blue-400/15" />
            <span className="mt-1 block text-right text-[11px] font-normal tabular-nums text-gray-500">{travelHistory.length} / {TRAVEL_HISTORY_MAX_LENGTH}</span>
          </label>
          {error ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950 dark:text-red-200">{error}</p> : null}
        </div>
        <footer className="flex items-center justify-between gap-4 border-t border-gray-200 bg-white/90 px-4 py-3 dark:border-white/10 dark:bg-[#1f1f1f]/95 sm:px-5">
          <p className="text-xs text-gray-500 dark:text-gray-400">Enter at least one field to import.</p>
          <button
            type="submit"
            disabled={submitting}
            aria-disabled={!hasInput || undefined}
            className={`rounded-xl px-5 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed ${hasInput ? "bg-[#1264df] text-white hover:bg-[#0f55bf]" : "bg-gray-300 text-gray-500 dark:bg-gray-700 dark:text-gray-400"}`}
          >
            {submitting ? "Importing..." : "Import"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function TravelPrioritySlider({ value, onChange }: {
  value: TravelPriorityValue;
  onChange: (value: TravelPriorityValue) => void;
}) {
  const selectedLevel = TRAVEL_PRIORITY_LEVELS.find((level) => level.value === value) || TRAVEL_PRIORITY_LEVELS[2];
  const selectedLabel = [selectedLevel.label, selectedLevel.detail].filter(Boolean).join(" ");

  return (
    <div className="mb-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-2 dark:border-white/10 dark:bg-white/[0.035]">
      <div className="mb-1">
        <label htmlFor="profile-travel-priority" className="text-[13px] font-medium leading-5 text-gray-900 dark:text-gray-100">Lower prices or faster trips?</label>
      </div>
      <div className="px-4 md:px-6">
        <div className="relative">
          <input
            id="profile-travel-priority"
            type="range"
            min={-2}
            max={2}
            step={1}
            value={value}
            onChange={(event) => onChange(Number(event.target.value) as TravelPriorityValue)}
            aria-valuetext={selectedLabel}
            className="profile-preference-slider profile-travel-priority-slider relative z-10 block h-8 w-full cursor-pointer accent-blue-600 dark:accent-blue-400 md:h-6"
          />
          <span aria-hidden="true" className="profile-slider-rail pointer-events-none absolute top-1/2 z-0 -translate-y-1/2" />
          <div aria-hidden="true" className="profile-slider-stops pointer-events-none absolute inset-y-0 z-20">
            {TRAVEL_PRIORITY_LEVELS.map((level, index) => (
              <span
                key={level.value}
                style={{ left: `${(index / (TRAVEL_PRIORITY_LEVELS.length - 1)) * 100}%` }}
                className={`absolute top-1/2 h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-500 dark:bg-gray-400 ${value === level.value ? "opacity-0" : ""}`}
              />
            ))}
          </div>
        </div>
        <div className="profile-slider-labels relative min-h-12 md:min-h-10">
          {TRAVEL_PRIORITY_LEVELS.map((level, index) => (
            <button
              key={level.value}
              type="button"
              onClick={() => onChange(level.value)}
              aria-label={`Set price or speed to ${[level.label, level.detail].filter(Boolean).join(" ")}`}
              style={{
                left: `${(index / (TRAVEL_PRIORITY_LEVELS.length - 1)) * 100}%`,
                width: "4rem",
                transform: "translateX(-50%)",
              }}
              className={`absolute top-0 block min-w-0 whitespace-normal rounded px-1 text-center text-[10px] leading-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:text-[11px] md:leading-4 ${value === level.value ? "text-blue-700 dark:text-blue-300" : "text-gray-500 dark:text-gray-400"}`}
            >
              <span className="block font-semibold">{level.label}</span>
              {level.detail ? <span className="block font-medium">{level.detail}</span> : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreferenceSlider({ id, label, value, positiveOnly = false, leading, onChange, onRemove }: {
  id: string;
  label: string;
  value: PreferenceValue;
  positiveOnly?: boolean;
  leading?: React.ReactNode;
  onChange: (value: PreferenceValue) => void;
  onRemove?: () => void;
}) {
  const levels = positiveOnly ? PREFERENCE_LEVELS.slice(2) : PREFERENCE_LEVELS;
  const selectedLevel = levels.find((level) => level.value === value) || PREFERENCE_LEVELS[2];

  return (
    <div className="isolate grid grid-cols-1 gap-0.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 dark:border-white/10 dark:bg-white/[0.035] md:grid-cols-[minmax(7rem,0.7fr)_minmax(12rem,1.5fr)_1.75rem] md:items-center md:gap-1">
      <div className="flex min-w-0 items-center gap-1.5">
        {leading}
        <label htmlFor={id} className="min-w-0 truncate text-[13px] font-medium leading-5 text-gray-900 dark:text-gray-100">{label}</label>
      </div>
      <div className="relative grid min-w-0 grid-cols-1 items-start md:contents">
        <div className={`min-w-0 ${onRemove ? "pr-6 md:pr-0" : ""}`}>
          <div className="px-4 md:px-6">
            <div className="relative">
              <input
                id={id}
                type="range"
                min={positiveOnly ? 0 : -2}
                max={2}
                step={1}
                value={value}
                data-preference-value={value}
                onChange={(event) => onChange(Number(event.target.value) as PreferenceValue)}
                aria-valuetext={selectedLevel.label}
                className="profile-preference-slider relative z-10 block h-8 w-full cursor-pointer accent-blue-600 dark:accent-blue-400 md:h-6"
              />
              <span aria-hidden="true" className="profile-slider-rail pointer-events-none absolute top-1/2 z-0 -translate-y-1/2" />
              <div aria-hidden="true" className="profile-slider-stops pointer-events-none absolute inset-y-0 z-20">
                {levels.map((level, index) => (
                  <span
                    key={level.value}
                    style={{ left: `${(index / (levels.length - 1)) * 100}%` }}
                    className={`absolute top-1/2 h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-500 dark:bg-gray-400 ${value === level.value ? "opacity-0" : ""}`}
                  />
                ))}
              </div>
            </div>
            <div className="profile-slider-labels relative h-8 md:h-6">
              {levels.map((level, index) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => onChange(level.value)}
                  aria-label={`Set ${label} to ${level.label}`}
                  title={level.label}
                  data-preference-level={level.value}
                  data-selected={value === level.value ? "" : undefined}
                  style={{ left: `${(index / (levels.length - 1)) * 100}%` }}
                  className="profile-preference-level absolute top-0 flex min-h-8 w-11 -translate-x-1/2 items-start justify-center whitespace-normal rounded px-0.5 text-center text-[10px] leading-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:min-h-6 md:w-auto md:whitespace-nowrap md:text-[11px] md:leading-4"
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${label}`}
            className="absolute right-0 top-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-red-300 bg-transparent text-gray-700 transition hover:border-red-400 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 dark:border-red-400/35 dark:text-gray-100 dark:hover:border-red-400/60 dark:hover:text-white md:static md:mt-0.5 md:h-7 md:w-7"
          >
            <svg aria-hidden="true" viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5" />
            </svg>
          </button>
        ) : <span aria-hidden="true" className="hidden md:block" />}
      </div>
    </div>
  );
}

function AirlineLogo({ code, name, lazy = false }: { code: string; name: string; lazy?: boolean }) {
  const [failed, setFailed] = useState(false);

  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white text-[9px] font-semibold text-gray-500">
      {failed ? code : (
        <img
          src={appPath(`/airline-logos/v1/${encodeURIComponent(code)}.svg`)}
          alt=""
          title={`${name} logo`}
          loading={lazy ? "lazy" : undefined}
          onError={() => setFailed(true)}
          className="h-5 w-5 object-contain"
        />
      )}
    </span>
  );
}

function airlineDisplayName(label: string) {
  return label.replace(/\s\([A-Z0-9]{2}\)$/, "");
}

function AirlinePicker({ selectedAirlines, onAdd }: { selectedAirlines: string[]; onAdd: (code: string) => void }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const matches = ORDERED_AIRLINES.filter((airline) => (
    !selectedAirlines.includes(airline.value) &&
    (!normalizedQuery || airline.label.toLowerCase().includes(normalizedQuery) || airline.value.toLowerCase().includes(normalizedQuery))
  ));

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div ref={pickerRef} className="relative">
      <label htmlFor="profile-airline-search" className="sr-only">Search and add airlines</label>
      <input
        id="profile-airline-search"
        type="search"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search airlines or codes"
        autoComplete="off"
        aria-controls="profile-airline-results"
        aria-expanded={open}
        className="h-11 w-full rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-white/15 dark:bg-white/5 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400 dark:focus:ring-blue-400/15"
      />
      {open ? (
        <div id="profile-airline-results" role="listbox" aria-label="Airline search results" className="absolute inset-x-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl dark:border-white/15 dark:bg-[#292929]">
          {matches.length ? matches.map((airline, index) => (
            <button
              key={airline.value}
              type="button"
              role="option"
              aria-selected="false"
              onClick={() => {
                onAdd(airline.value);
                setQuery("");
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 dark:hover:bg-white/10 dark:focus:bg-white/10"
            >
              <AirlineLogo code={airline.value} name={airlineDisplayName(airline.label)} lazy={!normalizedQuery && index >= 20} />
              {airlineDisplayName(airline.label)}
            </button>
          )) : (
            <p className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No airlines found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function ProfileDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [initialProfile] = useState(readStoredProfile);
  const [travellerType, setTravellerType] = useState<TravellerType>(initialProfile.travellerType);
  const [fareClass, setFareClass] = useState<FareClass>(initialProfile.fareClass);
  const [personaConfirmed, setPersonaConfirmed] = useState(initialProfile.personaConfirmed);
  const [travellerSectionExpanded, setTravellerSectionExpanded] = useState(!initialProfile.personaConfirmed);
  const [pendingPersona, setPendingPersona] = useState<TravellerType | null>(null);
  const [additionalPreferences, setAdditionalPreferences] = useState(initialProfile.additionalPreferences);
  const [preferences, setPreferences] = useState<Record<string, PreferenceValue>>(initialProfile.preferences);
  const [selectedAirlines, setSelectedAirlines] = useState<string[]>(initialProfile.selectedAirlines);
  const [normalizationOffsets] = useState(initialProfile.normalizationOffsets);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const travellerSectionButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    storeProfile({ travellerType, fareClass, personaConfirmed, additionalPreferences, preferences, selectedAirlines, normalizationOffsets });
  }, [travellerType, fareClass, personaConfirmed, additionalPreferences, preferences, selectedAirlines, normalizationOffsets]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const preferenceValue = (id: string) => preferences[id] || 0;
  const setPreference = (id: string, value: PreferenceValue) => {
    if ((preferences[id] || 0) === value) return;
    setPreferences((current) => ({ ...current, [id]: value }));
  };
  const applyPersona = (nextPersona: TravellerType) => {
    setPreferences({ ...PERSONA_PRESETS[nextPersona] });
    setPendingPersona(null);
    setPersonaConfirmed(true);
    setTravellerSectionExpanded(false);
    travellerSectionButtonRef.current?.focus();
  };
  const keepPersonaSettings = () => {
    setPendingPersona(null);
    setPersonaConfirmed(true);
    setTravellerSectionExpanded(false);
    travellerSectionButtonRef.current?.focus();
  };
  const selectTravellerType = (nextTravellerType: TravellerType) => {
    if (nextTravellerType === travellerType && personaConfirmed) return;

    setTravellerType(nextTravellerType);
    if (nextTravellerType === "budget") setFareClass("economy");
    if (nextTravellerType === "vip") setFareClass("business");
    setPersonaConfirmed(false);
    setPendingPersona(nextTravellerType);
  };
  const removeAirline = (code: string) => {
    setSelectedAirlines((current) => current.filter((airlineCode) => airlineCode !== code));
    setPreferences((current) => {
      const { [`airline-${code}`]: _, ...remaining } = current;
      return remaining;
    });
  };
  const travellerTypeIndex = TRAVELLER_TYPE_OPTIONS.findIndex((option) => option.value === travellerType);
  const pendingPersonaLabel = TRAVELLER_TYPE_OPTIONS.find((option) => option.value === pendingPersona)?.label;
  const travellerTypeLabel = TRAVELLER_TYPE_OPTIONS[travellerTypeIndex].label;
  const fareClassLabel = FARE_CLASS_OPTIONS.find((option) => option.value === fareClass)?.label;
  const travelPriority = travelPriorityFromPreferences(preferences);
  const setTravelPriority = (value: TravelPriorityValue) => {
    setPreferences((current) => ({ ...current, ...travelPriorityPreferences(value) }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-2 backdrop-blur-sm dark:bg-black/70 sm:p-5" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <style>{`
        .profile-preference-slider {
          appearance: none;
          -webkit-appearance: none;
          background: transparent;
          touch-action: pan-y;
        }

        .profile-preference-slider::-webkit-slider-runnable-track {
          height: 4px;
          border-radius: 9999px;
          background: transparent;
        }

        .dark .profile-preference-slider::-webkit-slider-runnable-track {
          background: transparent;
        }

        .profile-preference-slider::-webkit-slider-thumb {
          width: 18px;
          height: 18px;
          margin-top: -7px;
          appearance: none;
          -webkit-appearance: none;
          border: 2px solid #fff;
          border-radius: 9999px;
          background: #6b7280;
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.25);
        }

        .dark .profile-preference-slider::-webkit-slider-thumb {
          border-color: #27272a;
          background: #a1a1aa;
        }

        .profile-preference-slider[data-preference-value="-2"]::-webkit-slider-thumb { background: #dc2626; }
        .profile-preference-slider[data-preference-value="-1"]::-webkit-slider-thumb { background: #d97706; }
        .profile-preference-slider[data-preference-value="1"]::-webkit-slider-thumb { background: #1264df; }
        .profile-preference-slider[data-preference-value="2"]::-webkit-slider-thumb { background: #16a34a; }
        .dark .profile-preference-slider[data-preference-value="-2"]::-webkit-slider-thumb { background: #f87171; }
        .dark .profile-preference-slider[data-preference-value="-1"]::-webkit-slider-thumb { background: #fbbf24; }
        .dark .profile-preference-slider[data-preference-value="1"]::-webkit-slider-thumb { background: #3b82f6; }
        .dark .profile-preference-slider[data-preference-value="2"]::-webkit-slider-thumb { background: #4ade80; }

        .profile-preference-slider::-moz-range-track {
          height: 4px;
          border-radius: 9999px;
          background: transparent;
        }

        .dark .profile-preference-slider::-moz-range-track {
          background: transparent;
        }

        .profile-preference-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border: 2px solid #fff;
          border-radius: 9999px;
          background: #6b7280;
          box-shadow: 0 1px 3px rgb(0 0 0 / 0.25);
        }

        .dark .profile-preference-slider::-moz-range-thumb {
          border-color: #27272a;
          background: #a1a1aa;
        }

        .profile-preference-slider[data-preference-value="-2"]::-moz-range-thumb { background: #dc2626; }
        .profile-preference-slider[data-preference-value="-1"]::-moz-range-thumb { background: #d97706; }
        .profile-preference-slider[data-preference-value="1"]::-moz-range-thumb { background: #1264df; }
        .profile-preference-slider[data-preference-value="2"]::-moz-range-thumb { background: #16a34a; }
        .dark .profile-preference-slider[data-preference-value="-2"]::-moz-range-thumb { background: #f87171; }
        .dark .profile-preference-slider[data-preference-value="-1"]::-moz-range-thumb { background: #fbbf24; }
        .dark .profile-preference-slider[data-preference-value="1"]::-moz-range-thumb { background: #3b82f6; }
        .dark .profile-preference-slider[data-preference-value="2"]::-moz-range-thumb { background: #4ade80; }

        .profile-traveller-type-slider::-webkit-slider-thumb { background: #1264df; }
        .dark .profile-traveller-type-slider::-webkit-slider-thumb { background: #3b82f6; }
        .profile-traveller-type-slider::-moz-range-thumb { background: #1264df; }
        .dark .profile-traveller-type-slider::-moz-range-thumb { background: #3b82f6; }
        .profile-travel-priority-slider::-webkit-slider-thumb { background: #1264df; }
        .dark .profile-travel-priority-slider::-webkit-slider-thumb { background: #3b82f6; }
        .profile-travel-priority-slider::-moz-range-thumb { background: #1264df; }
        .dark .profile-travel-priority-slider::-moz-range-thumb { background: #3b82f6; }

        .profile-preference-level { color: #6b7280; }
        .profile-preference-level[data-selected] { font-weight: 600; }
        .profile-preference-level[data-selected][data-preference-level="-2"] { color: #dc2626; }
        .profile-preference-level[data-selected][data-preference-level="-1"] { color: #d97706; }
        .profile-preference-level[data-selected][data-preference-level="0"] { color: #6b7280; }
        .profile-preference-level[data-selected][data-preference-level="1"] { color: #1264df; }
        .profile-preference-level[data-selected][data-preference-level="2"] { color: #16a34a; }
        .dark .profile-preference-level { color: #a1a1aa; }
        .dark .profile-preference-level[data-selected][data-preference-level="-2"] { color: #f87171; }
        .dark .profile-preference-level[data-selected][data-preference-level="-1"] { color: #fbbf24; }
        .dark .profile-preference-level[data-selected][data-preference-level="0"] { color: #a1a1aa; }
        .dark .profile-preference-level[data-selected][data-preference-level="1"] { color: #60a5fa; }
        .dark .profile-preference-level[data-selected][data-preference-level="2"] { color: #4ade80; }

        .profile-slider-rail,
        .profile-slider-stops {
          left: 9px;
          right: 9px;
        }

        .profile-slider-rail {
          height: 4px;
          border-radius: 9999px;
          background: #cbd5e1;
        }

        .dark .profile-slider-rail {
          background: #52525b;
        }

        .profile-slider-labels {
          margin-left: 9px;
          margin-right: 9px;
        }

        .profile-preference-slider:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 4px;
          border-radius: 9999px;
        }

        @media (hover: none) and (pointer: coarse) {
          .profile-preference-slider::-webkit-slider-runnable-track {
            height: 6px;
          }

          .profile-preference-slider::-webkit-slider-thumb {
            width: 24px;
            height: 24px;
            margin-top: -9px;
          }

          .profile-preference-slider::-moz-range-track {
            height: 6px;
          }

          .profile-preference-slider::-moz-range-thumb {
            width: 24px;
            height: 24px;
          }

          .profile-slider-rail,
          .profile-slider-stops {
            left: 12px;
            right: 12px;
          }

          .profile-slider-rail {
            height: 6px;
          }

          .profile-slider-labels {
            margin-left: 12px;
            margin-right: 12px;
          }
        }
      `}</style>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-dialog-title"
        className="flex h-[calc(100dvh-1rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-[#f7f7f5] text-gray-900 shadow-2xl dark:border-white/10 dark:bg-[#171717] dark:text-gray-100 sm:h-[calc(100dvh-2.5rem)]"
      >
        <header className="flex flex-none items-start justify-between gap-4 border-b border-gray-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-[#1f1f1f]/95 sm:px-5">
          <div>
            <h2 id="profile-dialog-title" className="text-xl font-semibold tracking-tight">Help Reagent get to know you better</h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <svg aria-hidden="true" viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M2.5 2.5 9.5 9.5M9.5 2.5 2.5 9.5" />
            </svg>
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-5">
          <section className="mb-4 border-b border-gray-200 px-1 pb-4 dark:border-white/10">
            <button
              ref={travellerSectionButtonRef}
              type="button"
              aria-expanded={travellerSectionExpanded}
              aria-controls="profile-traveller-settings"
              disabled={!personaConfirmed}
              onClick={() => setTravellerSectionExpanded((expanded) => !expanded)}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-1 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-default"
            >
              <span>
                <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">What type of traveler are you?</span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {travellerSectionExpanded ? "This sets the baseline used when comparing trade-offs between flights." : `${travellerTypeLabel} - ${fareClassLabel}`}
                </span>
              </span>
              <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className={`h-4 w-4 shrink-0 text-gray-500 transition duration-300 dark:text-gray-400 ${travellerSectionExpanded ? "rotate-180" : ""} ${personaConfirmed ? "opacity-100" : "opacity-0"}`}>
                <path d="m5 7.5 5 5 5-5" />
              </svg>
            </button>
            <div
              id="profile-traveller-settings"
              aria-hidden={!travellerSectionExpanded}
              inert={!travellerSectionExpanded ? true : undefined}
              className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none ${travellerSectionExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
            >
              <div className="min-h-0 overflow-hidden">
                <div className="px-4 pt-2 md:px-6">
                  <div className="relative">
                    <input
                      id="profile-traveller-type"
                      type="range"
                      min={0}
                      max={TRAVELLER_TYPE_OPTIONS.length - 1}
                      step={1}
                      value={travellerTypeIndex}
                      onChange={(event) => selectTravellerType(TRAVELLER_TYPE_OPTIONS[Number(event.target.value)].value)}
                      aria-label="Traveler type"
                      aria-valuetext={travellerTypeLabel}
                      className="profile-preference-slider profile-traveller-type-slider relative z-10 block h-8 w-full cursor-pointer accent-blue-600 dark:accent-blue-400 md:h-6"
                    />
                    <span aria-hidden="true" className="profile-slider-rail pointer-events-none absolute top-1/2 z-0 -translate-y-1/2" />
                    <div aria-hidden="true" className="profile-slider-stops pointer-events-none absolute inset-y-0 z-20">
                      {TRAVELLER_TYPE_OPTIONS.map((option, index) => (
                        <span
                          key={option.value}
                          style={{ left: `${(index / (TRAVELLER_TYPE_OPTIONS.length - 1)) * 100}%` }}
                          className={`absolute top-1/2 h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-500 dark:bg-gray-400 ${travellerTypeIndex === index ? "opacity-0" : ""}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="profile-slider-labels relative h-8 md:h-6">
                    {TRAVELLER_TYPE_OPTIONS.map((option, index) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => selectTravellerType(option.value)}
                        aria-label={`Set traveler type to ${option.label}`}
                        data-selected={option.value === travellerType ? "" : undefined}
                        style={{ left: `${(index / (TRAVELLER_TYPE_OPTIONS.length - 1)) * 100}%` }}
                        className={`absolute top-0 -translate-x-1/2 rounded px-1 text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${option.value === travellerType ? "font-semibold text-blue-700 dark:text-blue-300" : "font-medium text-gray-500 dark:text-gray-400"}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <fieldset className="mt-2">
                  <legend className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">Default cabin class</legend>
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                    {FARE_CLASS_OPTIONS.map((option) => (
                      <label key={option.value} className={`cursor-pointer rounded-lg border px-2 py-2 text-center text-xs font-medium transition focus-within:ring-2 focus-within:ring-blue-500 ${fareClass === option.value ? "border-blue-500 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-400/10 dark:text-blue-200" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.035] dark:text-gray-300 dark:hover:border-white/20"}`}>
                        <input type="radio" name="profile-fare-class" value={option.value} checked={fareClass === option.value} onChange={() => setFareClass(option.value)} className="sr-only" />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">Used when you do not request a cabin class in the conversation.</p>
                </fieldset>

                {pendingPersona ? (
                  <div role="alert" className="mt-3 flex flex-col gap-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-950 dark:border-blue-400/25 dark:bg-blue-400/10 dark:text-blue-100 sm:flex-row sm:items-center">
                    <p className="min-w-0 flex-1">Would you like to replace your customized settings with the <strong>{pendingPersonaLabel}</strong> preset?</p>
                    <div className="flex shrink-0 justify-end gap-2">
                      <button type="button" onClick={keepPersonaSettings} className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-950 transition hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-400/25 dark:bg-transparent dark:text-blue-100 dark:hover:bg-blue-400/10">Keep my settings</button>
                      <button type="button" onClick={() => applyPersona(pendingPersona)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-blue-400 dark:text-blue-950 dark:hover:bg-blue-300">Use preset</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <div className="columns-1 gap-4 lg:columns-2">
            {PREFERENCE_SECTIONS.map((section) => (
              <React.Fragment key={section.title}>
                <section aria-labelledby={`profile-${section.title.toLowerCase().replaceAll(" ", "-")}`} className={`mb-4 inline-block w-full break-inside-avoid align-top ${section.title === "Alliances" ? "lg:break-before-column" : ""}`}>
                  <div className="mb-2 px-1">
                    <h3 id={`profile-${section.title.toLowerCase().replaceAll(" ", "-")}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100">{section.title}</h3>
                    <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">{section.description}</p>
                  </div>
                  <div className="space-y-1.5">
                    {section.title === "Priorities" ? (
                      <TravelPrioritySlider value={travelPriority} onChange={setTravelPriority} />
                    ) : null}
                    {section.preferences.map((preference) => (
                      <PreferenceSlider
                        key={preference.id}
                        id={`profile-${preference.id}`}
                        label={preference.label}
                        value={preferenceValue(preference.id)}
                        positiveOnly={preference.positiveOnly}
                        onChange={(value) => setPreference(preference.id, value)}
                      />
                    ))}
                  </div>
                </section>
                {section.title === "Alliances" ? (
                  <section aria-labelledby="profile-airlines" className="mb-4 inline-block w-full break-inside-avoid align-top">
                    <div className="mb-2 px-1">
                      <h3 id="profile-airlines" className="text-sm font-semibold text-gray-900 dark:text-gray-100">Airlines</h3>
                      <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">Search for airlines, add them, then set a preference for each one.</p>
                    </div>
                    <AirlinePicker
                      selectedAirlines={selectedAirlines}
                      onAdd={(code) => setSelectedAirlines((current) => [...current, code])}
                    />
                    {selectedAirlines.length ? (
                      <div className="mt-2 grid gap-1.5">
                        {selectedAirlines.map((code) => {
                          const airline = DEMO_AIRLINES.find((option) => option.value === code);
                          if (!airline) return null;

                          return (
                            <PreferenceSlider
                              key={code}
                              id={`profile-airline-${code}`}
                              label={airlineDisplayName(airline.label)}
                              value={preferenceValue(`airline-${code}`)}
                              leading={<AirlineLogo code={code} name={airlineDisplayName(airline.label)} />}
                              onChange={(value) => setPreference(`airline-${code}`, value)}
                              onRemove={() => removeAirline(code)}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-center text-xs text-gray-500 dark:border-white/15 dark:text-gray-400">No airline preferences added.</p>
                    )}
                  </section>
                ) : null}
              </React.Fragment>
            ))}
          </div>

          <section aria-labelledby="profile-additional-preferences" className="mb-2 mt-1 border-t border-gray-200 px-1 pt-4 dark:border-white/10">
            <div className="mb-2">
              <h3 id="profile-additional-preferences" className="text-sm font-semibold text-gray-900 dark:text-gray-100">Anything else you want me to factor in for every trip?</h3>
              <p className="mt-0.5 text-[11px] leading-4 text-gray-500 dark:text-gray-400">Add any enduring travel preferences that are not covered above.</p>
            </div>
            <textarea
              aria-labelledby="profile-additional-preferences"
              value={additionalPreferences}
              maxLength={ADDITIONAL_PREFERENCES_MAX_LENGTH}
              rows={4}
              onChange={(event) => setAdditionalPreferences(event.target.value)}
              placeholder="For example: I prefer quiet airport lounges and avoid very early departures when possible."
              className="block w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-5 text-gray-900 outline-none placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-white/15 dark:bg-white/5 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-400 dark:focus:ring-blue-400/15"
            />
            <p className="mt-1 text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{additionalPreferences.length} / {ADDITIONAL_PREFERENCES_MAX_LENGTH}</p>
          </section>
        </div>

        <footer className="flex flex-none items-center justify-between gap-4 border-t border-gray-200 bg-white/90 px-4 py-2 backdrop-blur dark:border-white/10 dark:bg-[#1f1f1f]/95 sm:px-5">
          <p className="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">These preferences help shape which flight options you see.</p>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-xl bg-[#1264df] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#0f55bf] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:hover:bg-[#2b78e8] dark:focus-visible:ring-offset-[#1f1f1f]"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
